import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getToken } from '../../utils/tokenUtils';
import { getRolePrefix } from '../../utils/routeUtils';
import { AuthContext } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  Search,
  Save,
  RefreshCw,
  HelpCircle,
  Bell,
  User,
  Calculator,
} from 'lucide-react';
import StatusBadge from '../../components/UI/StatusBadge';
import Notification from '../../components/UI/Notification';

const EditPayroll = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { roles } = useContext(AuthContext);
  const token = getToken();
  const rolePrefix = getRolePrefix(roles);
  
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Form state
  const [formData, setFormData] = useState({
    // Attendance
    total_working_days: '',
    days_present: '',
    leave_days: '',
    lop_days: '',
    overtime_hours: '',
    
    // Earnings
    basic_salary: '',
    hra: '',
    transport_allowance: '',
    medical_allowance: '',
    special_allowance: '',
    other_allowances: '',
    bonus: '',
    overtime_pay: '',
    
    // Deductions
    pf_employee: '',
    esi_employee: '',
    professional_tax: '',
    income_tax_tds: '',
    lop_deduction: '',
    other_deductions: '',
  });

  useEffect(() => {
    fetchPayrollDetails();
  }, [id]);

  const fetchPayrollDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      const payrollData = data?.data || data;
      setPayroll(payrollData);
      
      // Initialize form data from payroll
      if (payrollData) {
        const earnings = payrollData.earnings_breakdown || {};
        const deductions = payrollData.deductions_breakdown || {};
        
        setFormData({
          // Attendance
          total_working_days: String(payrollData.total_working_days || payrollData.working_days || ''),
          days_present: String(payrollData.days_present || ''),
          leave_days: String(payrollData.leave_days || payrollData.days_on_leave || ''),
          lop_days: String(payrollData.lop_days || ''),
          overtime_hours: String(payrollData.overtime_hours || payrollData.ot_hours || ''),
          
          // Earnings
          basic_salary: String(earnings.basic_salary || earnings['Basic Salary'] || payrollData.basic_salary || ''),
          hra: String(earnings.hra || earnings['House Rent Allowance (HRA)'] || ''),
          transport_allowance: String(earnings.transport_allowance || earnings['Transport Allowance'] || ''),
          medical_allowance: String(earnings.medical_allowance || earnings['Medical Allowance'] || ''),
          special_allowance: String(earnings.special_allowance || earnings['Special Allowance'] || ''),
          other_allowances: String(earnings.other_allowances || earnings['Other Allowances'] || ''),
          bonus: String(earnings.bonus || earnings['Bonus'] || ''),
          overtime_pay: String(earnings.overtime_pay || earnings['Overtime Pay'] || ''),
          
          // Deductions
          pf_employee: String(deductions.pf_employee || deductions['Provident Fund (Employee)'] || ''),
          esi_employee: String(deductions.esi_employee || deductions['ESI (Employee)'] || ''),
          professional_tax: String(deductions.professional_tax || deductions['Professional Tax'] || ''),
          income_tax_tds: String(deductions.income_tax_tds || deductions['Income Tax (TDS)'] || ''),
          lop_deduction: String(deductions.lop_deduction || deductions['Loss of Pay (LOP)'] || ''),
          other_deductions: String(deductions.other_deductions || deductions['Other Deductions'] || ''),
        });
      }
    } catch (error) {
      console.error('Error fetching payroll details:', error);
      setNotification({
        type: 'error',
        message: 'Failed to fetch payroll details',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Only allow numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    // Prevent multiple decimal points
    const parts = numericValue.split('.');
    const sanitizedValue = parts.length > 2 
      ? parts[0] + '.' + parts.slice(1).join('')
      : numericValue;
    
    setFormData(prev => ({
      ...prev,
      [field]: sanitizedValue === '' ? '' : sanitizedValue
    }));
  };

  const calculateTotalEarnings = () => {
    const total = (
      (parseFloat(formData.basic_salary) || 0) +
      (parseFloat(formData.hra) || 0) +
      (parseFloat(formData.transport_allowance) || 0) +
      (parseFloat(formData.medical_allowance) || 0) +
      (parseFloat(formData.special_allowance) || 0) +
      (parseFloat(formData.other_allowances) || 0) +
      (parseFloat(formData.bonus) || 0) +
      (parseFloat(formData.overtime_pay) || 0)
    );
    return Math.round(total * 100) / 100; // Round to 2 decimal places
  };

  const calculateTotalDeductions = () => {
    const total = (
      (parseFloat(formData.pf_employee) || 0) +
      (parseFloat(formData.esi_employee) || 0) +
      (parseFloat(formData.professional_tax) || 0) +
      (parseFloat(formData.income_tax_tds) || 0) +
      (parseFloat(formData.lop_deduction) || 0) +
      (parseFloat(formData.other_deductions) || 0)
    );
    return Math.round(total * 100) / 100; // Round to 2 decimal places
  };

  const calculateNetSalary = () => {
    const net = calculateTotalEarnings() - calculateTotalDeductions();
    return Math.round(net * 100) / 100; // Round to 2 decimal places
  };

  const handleCalculateLOP = () => {
    // Calculate LOP based on working days and days present
    const totalWorkingDays = parseFloat(formData.total_working_days) || 0;
    const daysPresent = parseFloat(formData.days_present) || 0;
    const leaveDays = parseFloat(formData.leave_days) || 0;
    const basicSalary = parseFloat(formData.basic_salary) || 0;
    
    const lopDays = Math.max(0, totalWorkingDays - daysPresent - leaveDays);
    const lopDeduction = lopDays > 0 && totalWorkingDays > 0 
      ? (lopDays * (basicSalary / totalWorkingDays))
      : 0;
    
    setFormData(prev => ({
      ...prev,
      lop_days: String(lopDays),
      lop_deduction: String(Math.round(lopDeduction * 100) / 100)
    }));
  };

  const handleCalculateOT = () => {
    // Calculate OT pay based on overtime hours and basic salary
    const totalWorkingDays = parseFloat(formData.total_working_days) || 0;
    const basicSalary = parseFloat(formData.basic_salary) || 0;
    const overtimeHours = parseFloat(formData.overtime_hours) || 0;
    
    const hourlyRate = totalWorkingDays > 0 ? (basicSalary / (totalWorkingDays * 8)) : 0;
    const otPay = overtimeHours * hourlyRate * 1.5; // 1.5x for overtime
    
    setFormData(prev => ({
      ...prev,
      overtime_pay: String(Math.round(otPay * 100) / 100)
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare earnings breakdown
      const earningsBreakdown = {
        'Basic Salary': formData.basic_salary,
        'House Rent Allowance (HRA)': formData.hra,
        'Transport Allowance': formData.transport_allowance,
        'Medical Allowance': formData.medical_allowance,
        'Special Allowance': formData.special_allowance,
        'Other Allowances': formData.other_allowances || 0,
        'Bonus': formData.bonus || 0,
        'Overtime Pay': formData.overtime_pay || 0,
      };

      // Prepare deductions breakdown
      const deductionsBreakdown = {
        'Provident Fund (Employee)': formData.pf_employee,
        'ESI (Employee)': formData.esi_employee || 0,
        'Professional Tax': formData.professional_tax,
        'Income Tax (TDS)': formData.income_tax_tds,
        'Loss of Pay (LOP)': formData.lop_deduction || 0,
        'Other Deductions': formData.other_deductions || 0,
      };

      const payload = {
        total_working_days: formData.total_working_days,
        days_present: formData.days_present,
        leave_days: formData.leave_days,
        lop_days: formData.lop_days,
        overtime_hours: formData.overtime_hours,
        earnings_breakdown: earningsBreakdown,
        deductions_breakdown: deductionsBreakdown,
        gross_salary: calculateTotalEarnings(),
        total_deductions: calculateTotalDeductions(),
        net_salary: calculateNetSalary(),
      };

      const response = await fetch(
        `https://hrmspayroll.prepstripe.com/api/payroll/monthly-payrolls/${id}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      const result = await response.json();

      if (response.ok) {
        setNotification({
          type: 'success',
          message: 'Payroll updated successfully',
        });
        // Navigate back to details page after a short delay
        setTimeout(() => {
          navigate(`${rolePrefix}/payroll/processing/details/${id}`);
        }, 1500);
      } else {
        throw new Error(result.message || 'Failed to update payroll');
      }
    } catch (error) {
      console.error('Error updating payroll:', error);
      setNotification({
        type: 'error',
        message: error.message || 'Failed to update payroll',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all changes?')) {
      fetchPayrollDetails();
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!payroll) {
    return (
      <div className="p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payroll Not Found</h3>
        </div>
      </div>
    );
  }

  const totalEarnings = calculateTotalEarnings();
  const totalDeductions = calculateTotalDeductions();
  const netSalary = calculateNetSalary();

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Top Header */}
      <div className="mb-6">
        {/* First Line: Title and Employee Details */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Edit Payroll
          </h1>
          <div className="mt-2 flex gap-x-3 items-center">
            <span>•</span>
            <p className="text-gray-600 dark:text-gray-400">
              {payroll.employee_name || 'N/A'}
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              {months[payroll.payroll_month - 1]} {payroll.payroll_year}
            </p>
          </div>
        </div>
        
        {/* Second Line: Back Button */}
        <div className="pb-4">
          <button
            onClick={() => navigate(`${rolePrefix}/payroll/processing/details/${id}`)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Details
          </button>
        </div>

        {/* Third Line: Search and Action Buttons */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search employees, payrolls..."
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white w-64"
            />
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
            />
          </div>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <HelpCircle className="h-4 w-4" />
          </button>
          <button className="relative flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <Bell className="h-4 w-4" />
            <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="lg:col-span-1 space-y-6">
          {/* Employee Information Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Employee Information
              </h2>
              <StatusBadge status={payroll.status} />
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <span className="text-blue-600 dark:text-blue-400 font-semibold">
                  {payroll.employee_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'N/A'}
                </span>
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {payroll.employee_name || 'N/A'}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {payroll.employee_id}
                </p>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Department:</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {payroll.department || payroll.department_name || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Designation:</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {payroll.designation || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Period:</span>
                <span className="text-gray-900 dark:text-white font-medium">
                  {months[payroll.payroll_month - 1]} {payroll.payroll_year}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Status:</span>
                <StatusBadge status={payroll.status} />
              </div>
            </div>
          </div>

          {/* Attendance Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Attendance
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Total Working Days
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.total_working_days}
                  onChange={(e) => handleInputChange('total_working_days', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Days Present
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.days_present}
                  onChange={(e) => handleInputChange('days_present', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Leave Days
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.leave_days}
                  onChange={(e) => handleInputChange('leave_days', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  LOP Days
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.lop_days}
                  onChange={(e) => handleInputChange('lop_days', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Overtime Hours
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.overtime_hours}
                  onChange={(e) => handleInputChange('overtime_hours', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleCalculateLOP}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Calculator className="h-4 w-4" />
                  Calculate LOP
                </button>
                <button
                  onClick={handleCalculateOT}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <Calculator className="h-4 w-4" />
                  Calculate OT
                </button>
              </div>
            </div>
          </div>

          {/* Salary Summary Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Salary Summary
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Gross Earnings:</span>
                <span className="text-green-600 dark:text-green-400 font-semibold">
                  ₹{totalEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Total Deductions:</span>
                <span className="text-red-600 dark:text-red-400 font-semibold">
                  -₹{totalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                <span className="text-gray-900 dark:text-white font-semibold">Net Salary:</span>
                <span className="text-teal-600 dark:text-teal-400 font-bold text-lg">
                  ₹{netSalary.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Middle Column - Earnings */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Earnings
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Basic Salary
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.basic_salary}
                  onChange={(e) => handleInputChange('basic_salary', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  House Rent Allowance (HRA)
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.hra}
                  onChange={(e) => handleInputChange('hra', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Transport Allowance
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.transport_allowance}
                  onChange={(e) => handleInputChange('transport_allowance', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Medical Allowance
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.medical_allowance}
                  onChange={(e) => handleInputChange('medical_allowance', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Special Allowance
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.special_allowance}
                  onChange={(e) => handleInputChange('special_allowance', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Other Allowances
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.other_allowances}
                  onChange={(e) => handleInputChange('other_allowances', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bonus
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.bonus}
                  onChange={(e) => handleInputChange('bonus', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Overtime Pay
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.overtime_pay}
                  onChange={(e) => handleInputChange('overtime_pay', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">Total Earnings</span>
                  <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                    ₹{totalEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Deductions */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Deductions
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Provident Fund (Employee)
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.pf_employee}
                  onChange={(e) => handleInputChange('pf_employee', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  ESI (Employee)
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.esi_employee}
                  onChange={(e) => handleInputChange('esi_employee', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Professional Tax
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.professional_tax}
                  onChange={(e) => handleInputChange('professional_tax', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Income Tax (TDS)
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.income_tax_tds}
                  onChange={(e) => handleInputChange('income_tax_tds', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Loss of Pay (LOP)
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.lop_deduction}
                  onChange={(e) => handleInputChange('lop_deduction', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Other Deductions
                </label>
                <input
                  type="text"
                  maxLength="50"
                  value={formData.other_deductions}
                  onChange={(e) => handleInputChange('other_deductions', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-900 dark:text-white"
                />
              </div>
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">Total Deductions</span>
                  <span className="text-lg font-semibold text-red-600 dark:text-red-400">
                    -₹{totalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPayroll;

